// src/components/inventory/ProductosTable.js
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import TablaInventario from './TablaInventario';

const COLUMNAS = [
  { clave: 'producto_id', etiqueta: 'ID', tipo: 'lectura', enCreacion: false },
  { clave: 'name', etiqueta: 'Producto', tipo: 'texto', requerido: true, placeholder: 'Ej. Velociraptor' },
  { clave: 'price', etiqueta: 'Precio', tipo: 'dinero', alinear: 'derecha', requerido: true, placeholder: '0', paso: '1' },
];

// La receta de cada producto: se muestra en la tabla como contexto.
const columnaExtra = {
  etiqueta: 'Receta',
  render: (p) => {
    const ings = p.Ingredientes || [];
    if (ings.length === 0) return <span className="texto-auxiliar">Sin ingredientes</span>;
    return (
      <ul className="receta">
        {ings.map((i) => (
          <li key={i.ingredient_id}>
            {i.name}
            <strong>×{i.ProductoIngrediente?.amount ?? 1}</strong>
          </li>
        ))}
      </ul>
    );
  },
};

function ProductosTable({ isReadOnly }) {
  const apiUrl = process.env.REACT_APP_API_URL;
  const [disponibles, setDisponibles] = useState([]);
  const [seleccion, setSeleccion] = useState({ ingredient_id: '', amount: '' });

  // Editor de receta de un producto existente.
  const [editandoReceta, setEditandoReceta] = useState(null);
  const [recetaBorrador, setRecetaBorrador] = useState([]);
  const [seleccionEd, setSeleccionEd] = useState({ ingredient_id: '', amount: '' });
  const [guardandoReceta, setGuardandoReceta] = useState(false);
  const recargar = useRef(null);
  const refDialogo = useRef(null);

  useEffect(() => {
    axios.get(`${apiUrl}/api/ingredientes`)
      .then(({ data }) => setDisponibles(Array.isArray(data) ? data : []))
      .catch(() => toast.error('No se pudieron cargar los ingredientes'));
  }, [apiUrl]);

  const nombreDe = (id) =>
    disponibles.find((i) => String(i.ingredient_id) === String(id))?.name ?? `#${id}`;

  const registrarRecarga = useCallback((fn) => { recargar.current = fn; }, []);

  const abrirReceta = (producto) => {
    setEditandoReceta(producto);
    setSeleccionEd({ ingredient_id: '', amount: '' });
    setRecetaBorrador(
      (producto.Ingredientes || []).map((i) => ({
        ingredient_id: i.ingredient_id,
        amount: i.ProductoIngrediente?.amount ?? 1,
      }))
    );
  };

  useEffect(() => {
    if (!editandoReceta) return;
    refDialogo.current?.querySelector('select, input, button')?.focus();
    const alPulsar = (e) => { if (e.key === 'Escape') setEditandoReceta(null); };
    document.addEventListener('keydown', alPulsar);
    return () => document.removeEventListener('keydown', alPulsar);
  }, [editandoReceta]);

  const guardarReceta = async () => {
    const p = editandoReceta;
    setGuardandoReceta(true);
    try {
      // El PUT exige nombre y precio además de la receta, así que se reenvían
      // los valores actuales sin cambiarlos.
      await axios.put(`${apiUrl}/api/productos/${p.producto_id}`, {
        name: p.name,
        price: p.price,
        ingredientes: recetaBorrador.map((r) => ({ id: r.ingredient_id, amount: r.amount })),
      });
      toast.success(`Receta de ${p.name} actualizada`);
      setEditandoReceta(null);
      recargar.current?.();
    } catch (e) {
      toast.error(e.response?.data?.error || 'No se pudo guardar la receta');
    } finally {
      setGuardandoReceta(false);
    }
  };

  // Formulario extra: el selector de receta. Antes los ingredientes se
  // agregaban a ciegas porque la lista nunca se pintaba.
  const formularioReceta = (nuevo, setNuevo) => {
    const receta = nuevo.ingredientes || [];

    const agregar = () => {
      const id = parseInt(seleccion.ingredient_id, 10);
      const cantidad = parseFloat(seleccion.amount);
      if (!id) return toast.error('Selecciona un ingrediente');
      if (!Number.isFinite(cantidad) || cantidad <= 0) return toast.error('La cantidad debe ser mayor a 0');
      if (receta.some((r) => r.ingredient_id === id)) return toast.error('Ese ingrediente ya está en la receta');
      setNuevo({ ...nuevo, ingredientes: [...receta, { ingredient_id: id, amount: cantidad }] });
      setSeleccion({ ingredient_id: '', amount: '' });
    };

    const quitar = (id) =>
      setNuevo({ ...nuevo, ingredientes: receta.filter((r) => r.ingredient_id !== id) });

    return (
      <div className="bloque-receta">
        <h4>Receta</h4>
        <div className="fila-receta">
          <div className="campo crece">
            <label htmlFor="ing-sel">Ingrediente</label>
            <select id="ing-sel" value={seleccion.ingredient_id}
              onChange={(e) => setSeleccion({ ...seleccion, ingredient_id: e.target.value })}>
              <option value="">Selecciona…</option>
              {disponibles.map((i) => (
                <option key={i.ingredient_id} value={i.ingredient_id}>{i.name}</option>
              ))}
            </select>
          </div>
          <div className="campo campo-corto">
            <label htmlFor="ing-cant">Cantidad</label>
            <input id="ing-cant" type="number" min="0" step="any" placeholder="1"
              value={seleccion.amount}
              onChange={(e) => setSeleccion({ ...seleccion, amount: e.target.value })} />
          </div>
          <button className="secundario" onClick={agregar}>Añadir</button>
        </div>

        {receta.length === 0 ? (
          <p className="texto-auxiliar">
            Todavía no has añadido ingredientes. Sin receta, la venta no descuenta inventario.
          </p>
        ) : (
          <ul className="receta-editable">
            {receta.map((r) => (
              <li key={r.ingredient_id}>
                <span>{nombreDe(r.ingredient_id)}</span>
                <strong>×{r.amount}</strong>
                <button className="fantasma pequeno" onClick={() => quitar(r.ingredient_id)}
                  aria-label={`Quitar ${nombreDe(r.ingredient_id)}`}>Quitar</button>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  const agregarAlBorrador = () => {
    const id = parseInt(seleccionEd.ingredient_id, 10);
    const cantidad = parseFloat(seleccionEd.amount);
    if (!id) return toast.error('Selecciona un ingrediente');
    if (!Number.isFinite(cantidad) || cantidad <= 0) return toast.error('La cantidad debe ser mayor a 0');
    if (recetaBorrador.some((r) => r.ingredient_id === id)) return toast.error('Ese ingrediente ya está en la receta');
    setRecetaBorrador((prev) => [...prev, { ingredient_id: id, amount: cantidad }]);
    setSeleccionEd({ ingredient_id: '', amount: '' });
  };

  return (
    <>
    <TablaInventario
      titulo="Productos"
      descripcion="Cada producto descuenta su receta del inventario al venderse."
      recurso="/api/productos"
      idKey="producto_id"
      columnas={COLUMNAS}
      columnaExtra={columnaExtra}
      etiquetaBuscar="Buscar producto…"
      nuevoVacio={{ name: '', price: '', ingredientes: [] }}
      extraFormulario={isReadOnly ? undefined : formularioReceta}
      aPayloadCrear={(n) => ({
        name: n.name.trim(),
        price: parseFloat(n.price) || 0,
        ingredientes: (n.ingredientes || []).map((i) => ({
          id: i.ingredient_id,
          amount: i.amount,
        })),
      })}
      registrarRecarga={registrarRecarga}
      accionesFila={isReadOnly ? undefined : (p) => (
        <button className="secundario pequeno" onClick={() => abrirReceta(p)}>Receta</button>
      )}
      isReadOnly={isReadOnly}
    />

    {editandoReceta && (
      <div className="modal-inv-overlay"
        onMouseDown={(e) => { if (e.target === e.currentTarget) setEditandoReceta(null); }}>
        <div className="modal-inv modal-receta" ref={refDialogo} role="dialog" aria-modal="true"
          aria-label={`Receta de ${editandoReceta.name}`}>
          <h3>Receta de {editandoReceta.name}</h3>
          <p className="texto-auxiliar">
            Esto es lo que se descuenta del inventario por cada unidad vendida.
          </p>

          <div className="fila-receta">
            <div className="campo crece">
              <label htmlFor="ed-ing">Ingrediente</label>
              <select id="ed-ing" value={seleccionEd.ingredient_id}
                onChange={(e) => setSeleccionEd({ ...seleccionEd, ingredient_id: e.target.value })}>
                <option value="">Selecciona…</option>
                {disponibles
                  .filter((i) => !recetaBorrador.some((r) => r.ingredient_id === i.ingredient_id))
                  .map((i) => <option key={i.ingredient_id} value={i.ingredient_id}>{i.name}</option>)}
              </select>
            </div>
            <div className="campo campo-corto">
              <label htmlFor="ed-cant">Cantidad</label>
              <input id="ed-cant" type="number" min="0" step="any" placeholder="1"
                value={seleccionEd.amount}
                onFocus={(e) => e.target.select()}
                onChange={(e) => setSeleccionEd({ ...seleccionEd, amount: e.target.value })}
                onKeyDown={(e) => { if (e.key === 'Enter') agregarAlBorrador(); }} />
            </div>
            <button className="secundario" onClick={agregarAlBorrador}>Añadir</button>
          </div>

          {recetaBorrador.length === 0 ? (
            <p className="aviso-bloque info">
              Sin ingredientes: vender este producto no descontará nada del inventario.
            </p>
          ) : (
            <ul className="receta-editable">
              {recetaBorrador.map((r) => (
                <li key={r.ingredient_id}>
                  <span>{nombreDe(r.ingredient_id)}</span>
                  <input className="cantidad-receta" type="number" min="0" step="any"
                    value={r.amount}
                    aria-label={`Cantidad de ${nombreDe(r.ingredient_id)}`}
                    onFocus={(e) => e.target.select()}
                    onChange={(e) => setRecetaBorrador((prev) => prev.map((x) =>
                      x.ingredient_id === r.ingredient_id
                        ? { ...x, amount: parseFloat(e.target.value) || 0 } : x))} />
                  <button className="fantasma pequeno"
                    onClick={() => setRecetaBorrador((prev) =>
                      prev.filter((x) => x.ingredient_id !== r.ingredient_id))}
                    aria-label={`Quitar ${nombreDe(r.ingredient_id)}`}>Quitar</button>
                </li>
              ))}
            </ul>
          )}

          <div className="fila-fin">
            <button className="cancelar" onClick={() => setEditandoReceta(null)}>Cancelar</button>
            <button onClick={guardarReceta} disabled={guardandoReceta} data-ocupado={guardandoReceta}>
              {guardandoReceta ? 'Guardando…' : 'Guardar receta'}
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}

export default ProductosTable;
